import { API_ENDPOINTS } from "@/config/api";
import { ApiError, fetchApi } from "./api";
import type {
  AlreadyRunning,
  BacklogTooLarge,
  CatalogPending,
  CatalogRunStarted,
  CatalogRunState,
  CatalogRunTarget,
  CatalogStale,
  CatalogSweepPlan,
  SweepNotPreviewed,
} from "@/types/nvgCatalog";

export async function fetchCatalogPending(): Promise<CatalogPending> {
  return fetchApi<CatalogPending>(API_ENDPOINTS.nvgCatalogPending);
}

/**
 * The staleness report (B1b). Served from a short server-side cache by
 * default: it costs an SSM round trip and a container that reads every stored
 * tube row, so a polling UI must leave `refresh` false.
 */
export async function fetchCatalogStale(
  refresh = false,
): Promise<CatalogStale> {
  return fetchApi<CatalogStale>(API_ENDPOINTS.nvgCatalogStale(refresh));
}

/**
 * The orphan sweep's dry-run plan. Same caching argument as the staleness
 * report — it costs an SSM round trip and a container that reads the live
 * corpus plus every stored tube row.
 *
 * Fetching it is also the precondition for `startCatalogSweep`: the server
 * validates the confirmation against this plan, so an operator cannot reach
 * the delete without having been shown what it deletes.
 */
export async function fetchCatalogSweepPlan(
  refresh = false,
): Promise<CatalogSweepPlan> {
  return fetchApi<CatalogSweepPlan>(API_ENDPOINTS.nvgCatalogSweep(refresh));
}

export async function startCatalogRun(
  target: CatalogRunTarget,
  limit: number | null,
): Promise<CatalogRunStarted> {
  return fetchApi<CatalogRunStarted>(API_ENDPOINTS.nvgCatalogRuns, {
    method: "POST",
    body: JSON.stringify({ target, limit }),
  });
}

/**
 * Start an orphan sweep.
 *
 * Separate from `startCatalogRun` because the payload is genuinely different:
 * a sweep takes no limit and *requires* `confirmReap` — the number of rows the
 * operator was shown as due. Folding it into the shared signature would make
 * that argument optional, which is exactly the thing it must not be.
 */
export async function startCatalogSweep(
  confirmReap: number,
): Promise<CatalogRunStarted> {
  return fetchApi<CatalogRunStarted>(API_ENDPOINTS.nvgCatalogRuns, {
    method: "POST",
    body: JSON.stringify({ target: "sweep", confirm_reap: confirmReap }),
  });
}

export async function fetchCatalogRun(
  runId: string,
  kind: CatalogRunTarget,
): Promise<CatalogRunState> {
  return fetchApi<CatalogRunState>(API_ENDPOINTS.nvgCatalogRun(runId, kind));
}

/**
 * FastAPI nests a dict `detail` one level down, so a structured error arrives
 * as `{ detail: { error: ... } }`. Both helpers below dig it out rather than
 * making every call site know that.
 */
function errorBody(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof ApiError)) return null;
  const data = error.data;
  if (typeof data !== "object" || data === null) return null;
  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail !== "object" || detail === null) return null;
  return detail as Record<string, unknown>;
}

/**
 * A refused delta run, or null if this was some other failure.
 *
 * This is the case the panel must *not* render as an error: the backlog is
 * simply larger than the shared instance may take, and the remedy — a bulk run
 * — is named in the body. The UI turns it into a button.
 */
export function asBacklogTooLarge(error: unknown): BacklogTooLarge | null {
  const body = errorBody(error);
  if (body?.error !== "backlog_too_large") return null;
  return body as unknown as BacklogTooLarge;
}

/** A run rejected because one of the same kind is already in flight. */
export function asAlreadyRunning(error: unknown): AlreadyRunning | null {
  const body = errorBody(error);
  if (body?.error !== "already_running") return null;
  return body as unknown as AlreadyRunning;
}

/**
 * A sweep refused because its confirmation no longer matches a current plan.
 *
 * Not an error to shout about: the plan moved between the preview and the
 * apply, which is precisely what the check exists to catch. The panel
 * re-fetches and shows the operator the new numbers.
 */
export function asSweepNotPreviewed(error: unknown): SweepNotPreviewed | null {
  const body = errorBody(error);
  if (body?.error !== "sweep_not_previewed") return null;
  return body as unknown as SweepNotPreviewed;
}
